import { ReactNode, useMemo } from 'react';

import createSafeContext from '#/utils/createSafeContext';

import {
  accountValueView,
  freeCollateralView,
  portfolioChangeDirectionView,
  portfolioChangePercentageView,
  portfolioChangeView,
} from './account-views';
import { AccountState, useAccountState } from './account-context';

export const [usePortfolioSummaryView, PortfolioSummaryViewCtx] =
  createSafeContext<PortfolioSummaryView>('PortfolioSummaryView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function PortfolioSummaryProvider(props: ProviderProps) {
  const accountState = useAccountState();

  const view = useMemo(() => createView(accountState), [accountState]);

  return (
    <PortfolioSummaryViewCtx value={view}>
      {props.children}
    </PortfolioSummaryViewCtx>
  );
}

export interface PortfolioSummaryView {
  readonly isAccountAvailable: boolean;
  readonly freeCollateral: string;
  readonly accountValue: string;
  readonly portfolioChange: string;
  readonly portfolioChangeDirection: 'up' | 'down';
  readonly portfolioChangePercentage: string;
}

const emptyView: PortfolioSummaryView = {
  isAccountAvailable: false,
  freeCollateral: '-',
  accountValue: '-',
  portfolioChange: '-',
  portfolioChangeDirection: 'up',
  portfolioChangePercentage: '-',
};

export function createView(state: AccountState): PortfolioSummaryView {
  const { account } = state;

  if (account == null) return emptyView;

  const freeCollateral = freeCollateralView(account);

  const accountValue = accountValueView(account);

  const portfolioChange = portfolioChangeView(account);

  const portfolioChangeDirection = portfolioChangeDirectionView(account);

  const portfolioChangePercentage = portfolioChangePercentageView(account);

  return {
    isAccountAvailable: true,
    freeCollateral,
    accountValue,
    portfolioChange,
    portfolioChangeDirection,
    portfolioChangePercentage,
  };
}
